import { Select } from '@zoopla-eng/zds-components/select';
import { useContext, createContext, useMemo } from 'react';
import { SelectGroup } from './SelectGroup';
import { SearchBarContext } from './SearchBar';
import { coerceQueryParameter } from '../utils/coerceQueryParameter';

const durations: Record<string, string> = {
    '900': '15 minutes',
    '1800': '30 minutes',
    '2700': '45 minutes',
    '3600': '1 hour',
    '4500': '1 ¼ hour'
};

const transportTypes: Record<string, string> = {
    driving: 'Driving',
    walking: 'Walking',
    walking_train: 'Train',
    public_transport: 'Public transport',
    cycling: 'Cycling'
};

const defaultDuration = {
    id: '900',
    text: durations[900]
};

const defaultTransportType = {
    id: 'driving',
    text: transportTypes.driving
};

const durationOptions = Object.entries(durations).map(([id, text]) => ({
    id,
    text
}));
const transportTypeOptions = Object.entries(transportTypes).map(
    ([id, text]) => ({ id, text })
);

type TravelTimeValue = {
    transportType: string;
    duration: string;
};

type TravelTimeContextType = {
    value: TravelTimeValue;
    onChange: (value: { duration: string; transportType: string }) => void;
};

const initialContextValue = {
    value: {
        duration: '',
        transportType: ''
    },
    onChange: () => null
};

const TravelTimeContext =
    createContext<TravelTimeContextType>(initialContextValue);

export const SelectTravelTime: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const { formValue, updateFormValue } = useContext(SearchBarContext);
    const value = useMemo(
        () => ({
            transportType: coerceQueryParameter(formValue.transport_type),
            duration: coerceQueryParameter(formValue.duration)
        }),
        [formValue.duration, formValue.transport_type]
    );

    const memoizedContextValue = useMemo(() => {
        const onChange = ({ transportType, duration }: TravelTimeValue) => {
            updateFormValue?.((oldFormValue) => ({
                ...oldFormValue,
                transport_type: transportType,
                duration: duration
            }));
        };

        return {
            onChange,
            value
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <TravelTimeContext.Provider value={memoizedContextValue}>
            {children}
        </TravelTimeContext.Provider>
    );
};

export const SelectDuration: React.FC = () => {
    const { value, onChange } = useContext(TravelTimeContext);
    return (
        <Select
            options={durationOptions}
            onSelectedOptionChange={(o) =>
                onChange({ ...value, duration: o.id })
            }
            selectedOption={
                durationOptions.find((o) => o.id === value.duration) ??
                defaultDuration
            }
            label="Max travel time"
        />
    );
};

export const SelectTransportType: React.FC = () => {
    const { value, onChange } = useContext(TravelTimeContext);

    const selectedOption = useMemo(() => {
        return (
            transportTypeOptions.find((o) => o.id === value.transportType) ??
            defaultTransportType
        );
    }, [value.transportType]);

    return (
        <Select
            options={transportTypeOptions}
            onSelectedOptionChange={(o) =>
                onChange({ ...value, transportType: o.id })
            }
            selectedOption={selectedOption}
            label="Transport type"
        />
    );
};

export const SelectGroupTravelTime: React.FC<{
    children: React.ReactNode;
    align?: 'left' | 'right';
}> = ({ children, align = 'left' }) => {
    const {
        value: { duration, transportType }
    } = useContext(TravelTimeContext);

    return (
        <SelectGroup
            title="Travel time"
            value={`${durations[duration] || defaultDuration.text} • ${
                transportTypes[transportType] || defaultTransportType.text
            }`}
            align={align}
        >
            {children}
        </SelectGroup>
    );
};
